import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { directorsAPI } from '@/lib/api';
import { toast } from 'sonner';
import logger from '@/lib/logger';

const empty = {
  name: '', din: '', type: 'Director',
  pan: '', email: '', phone: '', date_of_birth: '',
  notes: '', active: true,
};

export default function DirectorFormDialog({ open, onOpenChange, director, onSaved }) {
  const editMode = !!director;
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      if (director) {
        setForm({
          name: director.name || '',
          din: director.din || '',
          type: director.type || 'Director',
          pan: director.pan || '',
          email: director.email || '',
          phone: director.phone || '',
          date_of_birth: director.date_of_birth || '',
          notes: director.notes || '',
          active: director.active !== false,
        });
      } else {
        setForm(empty);
      }
      setError('');
    }
  }, [open, director]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.name.trim()) return 'Name is required';
    const din = form.din.trim().toUpperCase();
    if (!din) return 'DIN is required';
    if (!/^\d{8}$/.test(din)) return 'DIN must be exactly 8 digits';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setSubmitting(true);
    const payload = { ...form, din: form.din.trim().toUpperCase() };
    try {
      if (editMode) {
        await directorsAPI.update(director.id, payload);
        toast.success('Director updated');
      } else {
        await directorsAPI.create(payload);
        toast.success('Director created');
      }
      onSaved && onSaved();
    } catch (e2) {
      logger.error('Save director failed', e2);
      setError(e2.response?.data?.detail || 'Save failed');
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{editMode ? 'Edit Director' : 'Add Director / Designated Partner'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label htmlFor="d-name" className="text-xs">Name *</Label>
              <Input id="d-name" value={form.name} onChange={e => update('name', e.target.value)} placeholder="Full legal name" data-testid="director-name" />
            </div>
            <div>
              <Label htmlFor="d-din" className="text-xs">DIN / DPIN *</Label>
              <Input id="d-din" value={form.din} onChange={e => update('din', e.target.value.toUpperCase())} placeholder="8-digit DIN" maxLength={8} className="font-mono" data-testid="director-din" />
            </div>
            <div>
              <Label htmlFor="d-type" className="text-xs">Type</Label>
              <Select value={form.type} onValueChange={v => update('type', v)}>
                <SelectTrigger id="d-type" data-testid="director-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Director">Director</SelectItem>
                  <SelectItem value="Designated Partner">Designated Partner</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="d-pan" className="text-xs">PAN</Label>
              <Input id="d-pan" value={form.pan} onChange={e => update('pan', e.target.value.toUpperCase())} placeholder="ABCDE1234F" maxLength={10} className="font-mono" data-testid="director-pan" />
            </div>
            <div>
              <Label htmlFor="d-dob" className="text-xs">Date of Birth</Label>
              <Input id="d-dob" type="date" value={form.date_of_birth} onChange={e => update('date_of_birth', e.target.value)} data-testid="director-dob" />
            </div>
            <div>
              <Label htmlFor="d-email" className="text-xs">Email</Label>
              <Input id="d-email" type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="director@example.com" data-testid="director-email" />
            </div>
            <div>
              <Label htmlFor="d-phone" className="text-xs">Phone</Label>
              <Input id="d-phone" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+91 …" data-testid="director-phone" />
            </div>
            <div className="col-span-2">
              <Label htmlFor="d-notes" className="text-xs">Notes</Label>
              <Textarea id="d-notes" value={form.notes} onChange={e => update('notes', e.target.value)} rows={2} data-testid="director-notes" />
            </div>
            <div className="col-span-2 flex items-center justify-between rounded-lg border px-3 py-2">
              <div>
                <p className="text-sm font-medium">Active</p>
                <p className="text-[11px] text-muted-foreground">Inactive directors are hidden from default lists & dropdowns</p>
              </div>
              <Switch checked={form.active} onCheckedChange={v => update('active', v)} data-testid="director-active" />
            </div>
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="director-save-btn">
              {submitting ? 'Saving…' : editMode ? 'Save changes' : 'Create director'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
